import { useState } from "react";
import { CheckCircle2, Circle, KeyRound, PackageSearch, XCircle } from "lucide-react";
import Reveal from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { api, formatApiErrorDetail, usd } from "@/lib/api";
import { useLang } from "@/i18n";

const STATUS_STEP = {
  quote_requested: 0,
  awaiting_payment: 0,
  pending: 0,
  paid: 1,
  confirmed: 2,
  completed: 3,
};

export default function Tracking() {
  const { t } = useLang();
  const STEPS = [t("step1"), t("step2"), t("step3"), t("step4")];
  const STATUS_LABEL = {
    quote_requested: t("st_quote"),
    awaiting_payment: t("st_awaiting"),
    pending: t("st_pending"),
    paid: t("st_paid"),
    confirmed: t("st_confirmed"),
    completed: t("st_completed"),
    cancelled: t("st_cancelled"),
  };
  const [orderNumber, setOrderNumber] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [order, setOrder] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setOrder(null);
    try {
      const { data } = await api.get("/orders/track", {
        params: { order_number: orderNumber, email },
      });
      setOrder(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const field =
    "bg-[#0B1120] border-white/10 text-slate-100 placeholder:text-slate-600 focus-visible:ring-[#ED1C24] rounded-none h-12";

  const activeStep = order ? STATUS_STEP[order.status] ?? 0 : 0;

  return (
    <main data-testid="tracking-page" className="min-h-screen bg-[#0B1120] px-6 md:px-12 lg:px-24 pt-32 pb-24">
      <Reveal y={24}>
        <p className="text-xs tracking-[0.25em] uppercase text-[#ED1C24] mb-4">{t("tracking_label")}</p>
        <h1 className="font-display font-semibold text-white text-4xl sm:text-6xl tracking-tight mb-4">
          {t("tracking_title").replace(".", "")}<span className="text-[#FFC61A]">.</span>
        </h1>
        <p className="text-slate-400 max-w-xl mb-12">
          {t("tracking_sub")}
        </p>
        <div data-testid="tracking-external-card" className="max-w-xl mb-12 border border-[#FFC61A]/25 bg-[#FFC61A]/5 p-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase text-[#FFC61A] mb-1.5">{t("track_ext_label")}</p>
            <p className="text-sm text-slate-400 max-w-md">{t("track_ext_text")}</p>
          </div>
          <a
            data-testid="tracking-external-link"
            href="https://tracking.kinos.tl"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-[#ED1C24] hover:bg-[#C81016] text-white text-sm font-medium px-6 py-3 transition-colors duration-300"
          >
            {t("track_ext_btn")}
          </a>
        </div>
      </Reveal>

      <div className="grid lg:grid-cols-2 gap-10 items-start">
        <Reveal delay={0.1}>
          <form
            data-testid="tracking-form"
            onSubmit={submit}
            className="bg-[#0F172A] border border-white/10 p-8 space-y-5"
          >
            <Input
              data-testid="tracking-order-number-input"
              required
              placeholder={t("track_ph_order")}
              value={orderNumber}
              onChange={(e) => setOrderNumber(e.target.value)}
              className={field}
            />
            <Input
              data-testid="tracking-email-input"
              required
              type="email"
              placeholder={t("track_ph_email")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={field}
            />
            <button
              data-testid="tracking-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-3 bg-[#ED1C24] hover:bg-[#C81016] disabled:opacity-60 text-white font-medium py-4 transition-all duration-300 hover:shadow-[0_0_30px_rgba(237,28,36,0.4)]"
            >
              <PackageSearch size={17} />
              {loading ? t("track_searching") : t("track_btn")}
            </button>
            {error && (
              <p data-testid="tracking-error" className="text-[#ED1C24] text-sm">{error}</p>
            )}
          </form>
        </Reveal>

        {order && (
          <Reveal delay={0.05}>
            <div data-testid="tracking-result" className="bg-[#0F172A] border border-white/10 p-8">
              <div className="flex flex-wrap items-baseline justify-between gap-3 mb-2">
                <h2 className="font-display text-2xl text-white">
                  {order.order_number}
                </h2>
                <span className="text-xs text-slate-500">
                  {t("track_placed")} {new Date(order.created_at).toLocaleDateString()} · {order.method === "card" ? "Card" : order.method === "quote" ? "Quotation" : "Pay on delivery"}
                </span>
              </div>
              <p className="text-sm text-slate-400 mb-8">
                {order.customer_name} — {STATUS_LABEL[order.status] || order.status}
              </p>

              {order.status === "cancelled" ? (
                <div data-testid="tracking-cancelled-banner" className="flex items-center gap-3 border border-[#ED1C24]/40 bg-[#ED1C24]/10 p-5">
                  <XCircle className="text-[#ED1C24]" size={22} />
                  <p className="text-sm text-slate-200">{t("st_cancelled")}</p>
                </div>
              ) : (
                <div data-testid="tracking-timeline" className="space-y-0 mb-8">
                  {STEPS.map((step, i) => {
                    const done = i <= activeStep;
                    return (
                      <div key={step} className="flex gap-4">
                        <div className="flex flex-col items-center">
                          {done ? (
                            <CheckCircle2 size={20} className={i === activeStep ? "text-[#FFC61A]" : "text-[#ED1C24]"} />
                          ) : (
                            <Circle size={20} className="text-slate-700" />
                          )}
                          {i < STEPS.length - 1 && (
                            <div className={`w-px h-8 ${i < activeStep ? "bg-[#ED1C24]" : "bg-white/10"}`} />
                          )}
                        </div>
                        <p className={`text-sm pt-0.5 ${done ? "text-white" : "text-slate-600"}`}>
                          {step}
                        </p>
                      </div>
                    );
                  })}
                </div>
              )}

              {order.license_keys?.length > 0 && (
                <div data-testid="tracking-license-keys" className="border border-[#FFC61A]/30 bg-[#FFC61A]/5 p-5 mb-8">
                  <div className="flex items-center gap-2 mb-3">
                    <KeyRound size={15} className="text-[#FFC61A]" />
                    <p className="text-xs tracking-[0.2em] uppercase text-[#FFC61A]">{t("track_licenses")}</p>
                  </div>
                  <div className="space-y-2">
                    {order.license_keys.map((k, i) => (
                      <div key={i} data-testid={`license-key-${i}`} className="flex flex-wrap justify-between gap-2 text-sm">
                        <span className="text-slate-300">{k.label}</span>
                        <span className="font-mono text-emerald-400 select-all">{k.key}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="border-t border-white/10 pt-5 space-y-3">
                {order.items.map((i) => (
                  <div key={i.product_id} className="flex justify-between text-sm">
                    <span className="text-slate-300">
                      {i.name} <span className="text-slate-600">× {i.qty}</span>
                    </span>
                    <span className="text-slate-400">{usd(i.price * i.qty)}</span>
                  </div>
                ))}
                {order.discount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-emerald-400">{t("discount")} ({order.promo_code})</span>
                    <span className="text-emerald-400">-{usd(order.discount)}</span>
                  </div>
                )}
                <div className="flex justify-between border-t border-white/10 pt-3">
                  <span className="text-sm text-slate-400">{t("total")}</span>
                  <span data-testid="tracking-total" className="font-display text-xl text-[#FFC61A]">
                    {usd(order.total)}
                  </span>
                </div>
              </div>
            </div>
          </Reveal>
        )}
      </div>
    </main>
  );
}
